import React, { useState, useEffect } from 'react';
import {
  Clock, User, ArrowRight, RefreshCw, ChevronDown, ChevronUp,
  AlertCircle, CheckCircle, FileText, UserCheck, MessageSquare, Trash2
} from 'lucide-react';
import axios from 'axios';

/**
 * StatusHistoryTimeline - Displays history of case stage and client status changes
 *
 * Props:
 * - entityType: 'case' | 'client'
 * - entityId: The case_id or contact_id
 * - limit: Max number of records to show (default: 20)
 * - showHeader: Whether to show the header (default: true)
 * - compact: Use compact styling (default: false)
 * - portal: If true, uses client portal API (no auth token, visible_to_client only)
 */
const StatusHistoryTimeline = ({
  entityType,
  entityId,
  limit = 20,
  showHeader = true,
  compact = false,
  portal = false
}) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (entityId) {
      fetchHistory();
    }
  }, [entityType, entityId, limit, portal]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      let endpoint;
      let headers = {};

      if (portal) {
        // Portal mode - use portal-specific endpoint (no auth required)
        endpoint = `/api/status-history/portal/case/${entityId}`;
      } else {
        // Staff mode - use authenticated endpoints
        const token = localStorage.getItem('token');
        endpoint = entityType === 'case'
          ? `/api/status-history/case/${entityId}`
          : `/api/status-history/client/${entityId}`;
        headers = { Authorization: `Bearer ${token}` };
      }

      const response = await axios.get(endpoint, {
        params: { limit, ...(portal ? {} : { include_private: true }) },
        headers
      });

      setHistory(response.data.history || []);
    } catch (err) {
      console.error('Failed to load status history:', err);
      setError('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (historyId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) {
      return;
    }

    try {
      setDeleting(historyId);
      const token = localStorage.getItem('token');
      await axios.delete(`/api/status-history/${historyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove from local state
      setHistory(prev => prev.filter(item => item.id !== historyId));
    } catch (err) {
      console.error('Failed to delete entry:', err);
      alert('Failed to delete entry');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const getFieldIcon = (fieldName) => {
    switch (fieldName) {
      case 'status':
        return <CheckCircle className="w-4 h-4" />;
      case 'matter_stage':
        return <FileText className="w-4 h-4" />;
      case 'client_status':
        return <UserCheck className="w-4 h-4" />;
      case 'client_update':
        return <MessageSquare className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (value, fieldName) => {
    // Client updates get a distinct color
    if (fieldName === 'client_update') {
      return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
    }
    const colors = {
      // Case statuses
      'active': 'bg-green-500/20 text-green-400 border-green-500/30',
      'pending': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'on_hold': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      'closed': 'bg-slate-500/20 text-slate-400 border-slate-500/30',
      'archived': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      // Client statuses
      'prospective': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'former': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    };
    return colors[value?.toLowerCase()] || 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
  };

  if (loading) {
    return (
      <div className={`${compact ? 'p-3' : 'p-4'} flex items-center justify-center`}>
        <RefreshCw className="w-5 h-5 text-cyan-400 animate-spin" />
        <span className="ml-2 text-slate-400 text-sm">Loading history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${compact ? 'p-3' : 'p-4'} flex items-center text-red-400`}>
        <AlertCircle className="w-5 h-5 mr-2" />
        <span className="text-sm">{error}</span>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/50 rounded-lg border border-slate-700/50">
      {showHeader && (
        <div
          className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50 cursor-pointer hover:bg-slate-700/30 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center space-x-2">
            <Clock className="w-5 h-5 text-cyan-400" />
            <h3 className="font-medium text-white">Status History</h3>
            <span className="text-xs text-slate-400 bg-slate-700 px-2 py-0.5 rounded">
              {history.length} {history.length === 1 ? 'change' : 'changes'}
            </span>
          </div>
          <button className="p-1 hover:bg-slate-600 rounded transition-colors">
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </button>
        </div>
      )}

      {expanded && (
        <div className={`${compact ? 'p-3' : 'p-4'}`}>
          {history.length === 0 ? (
            <div className="text-center py-6">
              <Clock className="w-8 h-8 text-slate-500 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">No status changes recorded yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((item, index) => (
                <div
                  key={item.id}
                  className={`relative ${index < history.length - 1 ? 'pb-3' : ''}`}
                >
                  {/* Timeline line */}
                  {index < history.length - 1 && (
                    <div className="absolute left-[11px] top-6 bottom-0 w-0.5 bg-slate-700" />
                  )}

                  <div className="flex items-start space-x-3">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${getStatusColor(item.new_value, item.field_name)}`}>
                      {getFieldIcon(item.field_name)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {item.field_name === 'client_update' ? (
                        /* Client updates show as text content */
                        <div>
                          <span className="text-xs font-medium text-cyan-400 uppercase tracking-wide">Client Update</span>
                          <p className="text-sm text-slate-200 mt-1">{item.new_value}</p>
                        </div>
                      ) : (
                        /* Status changes show as badges */
                        <div className="flex items-center flex-wrap gap-2">
                          {item.old_value && (
                            <>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(item.old_value, item.field_name)}`}>
                                {item.old_value}
                              </span>
                              <ArrowRight className="w-3 h-3 text-slate-500" />
                            </>
                          )}
                          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(item.new_value, item.field_name)}`}>
                            {item.new_value}
                          </span>
                        </div>
                      )}

                      {item.change_reason && (
                        <p className="text-sm text-slate-300 mt-1">{item.change_reason}</p>
                      )}

                      <div className="flex items-center space-x-2 mt-1 text-xs text-slate-500">
                        {item.changed_by_name && (
                          <>
                            <User className="w-3 h-3" />
                            <span>{item.changed_by_name}</span>
                            <span>•</span>
                          </>
                        )}
                        <span title={formatDate(item.created_at)}>
                          {formatRelativeTime(item.created_at)}
                        </span>
                        {!portal && !item.visible_to_client && (
                          <>
                            <span>•</span>
                            <span className="text-amber-400">Staff only</span>
                          </>
                        )}
                        {/* Delete button - only for staff, not portal */}
                        {!portal && (
                          <button
                            onClick={() => handleDelete(item.id)}
                            disabled={deleting === item.id}
                            className="ml-2 p-1 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                            title="Delete entry"
                          >
                            {deleting === item.id ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <Trash2 className="w-3 h-3" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {history.length > 0 && (
            <button
              onClick={fetchHistory}
              className="w-full mt-4 py-2 text-sm text-cyan-400 hover:text-cyan-300 hover:bg-slate-700/50 rounded transition-colors flex items-center justify-center space-x-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default StatusHistoryTimeline;
